import React, { ReactNode, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  TriageStateFilter,
  formatUnreadBadge,
  useMarkTriageRead,
  useMarkTriageUnread,
  useTriageList,
} from '../../api/useTriageList';
import {
  CounterStuckAutoClear,
  CounterStuckNotification,
  CounterStuckPayload,
  OrphanedAutomationFillPayload,
  OrphanedFillNotification,
  TriageBucketPage,
  TriageMarketLink,
  TriageNotification,
  CounterStuckUnstuckClear,
  autoClearOf,
  hasNoMarketHedgeWindow,
  isAutoCleared,
  isUnstuckCleared,
  unstuckClearOf,
} from '../../types/notifications.types';
import { TriageGroupChip } from '../triage/TriageGroupChip';
import { platformColor, platformLabel, sideColor } from './platform-colors';
import {
  TRIAGE_SCOPE_NOUN,
  TRIAGE_UNGROUPED_KEY,
  TRIAGE_UNGROUPED_LABEL,
  TriageScope,
  applyTriageScope,
  readTriageScope,
  withoutTriageScope,
} from '../triage/triageDeepLink';

/**
 * The tray's own palette.
 *
 * 🚨 Desaturated on purpose. MUI's `warning.main` / `error.main` are built to
 * be the loudest thing on a screen, and this tray puts several of them on
 * every row — so nothing reads as urgent, which is the same as everything
 * being quiet. These sit a step down in saturation, and the ONE line that is
 * genuinely an alarm (a cycle that commits above $1) keeps the strongest of
 * them.
 *
 * Filled MUI chips are avoided for the same reason plus a second one: a
 * `color="warning"` chip renders BLACK text on saturated orange, which on
 * this app's dark chrome is both the loudest and the least legible thing in
 * the header. Everything here is outlined with a faint tint instead.
 */
const TONE = {
  /** Headline text. Never the theme's `text.primary` — it resolves dark. */
  title: 'common.white',
  /** The ordinary caption body: readable, not shouting. */
  body: 'grey.300',
  /** Labels ("naked", "counter", "sum"): present but recessive. */
  label: 'grey.500',
  /** Something needs a human, but nothing is on fire. */
  warn: '#e3a857',
  /** Money is committed wrong. The loudest thing the tray may render. */
  alarm: '#e5736b',
  /** Good news — an all-clear. */
  ok: '#6fcf97',
} as const;

/**
 * The market pages for a card's legs.
 *
 * 🚨 Renders exactly what the server resolved and never builds a URL. A
 * Polymarket `conditionId` link 404s on click, so a pair that does not know
 * its slug gets NO Polymarket chip — advertising a broken link is worse than
 * advertising none, and this row is read by someone trying to reach the
 * market fast.
 */
function MarketLinks({ markets }: { markets?: TriageMarketLink[] }) {
  if (!markets?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
      {markets.map((m) => {
        const color = platformColor(m.platform) ?? TONE.body;
        return (
          <Chip
            key={`${m.platform}:${m.url}`}
            size="small"
            variant="outlined"
            clickable
            component="a"
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            data-market-link={m.platform.toLowerCase()}
            label={
              <>
                {platformLabel(m.platform)}
                <OpenInNewIcon sx={{ fontSize: '0.7rem', ml: 0.3, mb: '-1px' }} />
              </>
            }
            sx={{
              height: 18,
              borderColor: `${color}66`,
              color,
              backgroundColor: `${color}14`,
              '& .MuiChip-label': { px: 0.75, fontSize: '0.66rem', fontWeight: 700 },
            }}
          />
        );
      })}
    </Stack>
  );
}

/** A faint tinted pill: a colour the eye can find, at a volume it can live with. */
function tintedChipSx(color: string) {
  return {
    height: 18,
    borderColor: `${color}66`,
    color,
    backgroundColor: `${color}14`,
    '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem' },
  } as const;
}

/** The venue, as a coloured word — Kalshi emerald, Poly blue. */
function Venue({ platform }: { platform: unknown }) {
  const color = platformColor(platform);
  return (
    <Box
      component="span"
      data-venue={String(platform ?? '').toLowerCase() || 'unknown'}
      sx={{ color: color ?? TONE.body, fontWeight: 700 }}
    >
      {platformLabel(platform)}
    </Box>
  );
}

/**
 * YES / NO as a bordered pill.
 *
 * The shape is what keeps it apart from the venue: Kalshi is also green, and
 * two green WORDS side by side would be one blur. A pill next to a bare word
 * reads as two different kinds of fact.
 */
function Side({ side }: { side: unknown }) {
  const label = String(side ?? '').toUpperCase();
  if (!label) return null;
  const color = sideColor(side) ?? TONE.label;
  return (
    <Box
      component="span"
      data-side={label.toLowerCase()}
      sx={{
        display: 'inline-block',
        border: `1px solid ${color}66`,
        backgroundColor: `${color}14`,
        color,
        borderRadius: 0.5,
        px: 0.5,
        fontSize: '0.62rem',
        fontWeight: 700,
        lineHeight: 1.5,
      }}
    >
      {label}
    </Box>
  );
}

/**
 * The triage tray (spec §5): two buckets — *Counter stuck* and *Orphaned
 * fills* — over a server-side, global read state the operator clears
 * deliberately.
 *
 * 🚨 NOTHING is ever marked read by looking. Opening this tab, hovering a row
 * or scrolling changes no state. That is the single behavioural difference
 * from the sidebar bell (which auto-marks everything the instant its popover
 * opens) and it is the entire point of the feature.
 */

/** URL param: show the cards already cleared, alongside the open ones. */
export const TRIAGE_SHOW_READ_PARAM = 'triRead';

/**
 * URL param: widen the tray back to cards whose pair is RETIRED — finished,
 * archived, expired, or settled on Kalshi.
 *
 * OFF by default, and that default is the feature. A card names an uncovered
 * leg on a market somebody can still act on; once the market is over there is
 * no action left, and a week of those reads as a backlog nobody opens. The
 * toggle exists because hiding is not deleting: the cards stay UNREAD in
 * Mongo, and one click brings them back.
 */
export const TRIAGE_INCLUDE_RETIRED_PARAM = 'triRetired';

/** Relative "5m ago" timestamp — no date lib in the repo, so inline. */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function qty(value: number | null | undefined): string {
  return value === null || value === undefined ? '?' : String(value);
}

/** A finite number, or `null` — an absent field is never a `0` we render. */
function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A price the venue actually quoted, or `null`.
 *
 * 🚨 `0` is NOT a price here, and it is not hypothetical: the engine's
 * `makerFillPrice` is a plain `f64` (not an `Option`) read off
 * `maker_filled_price`, whose initial value is `0.0` — so a slot whose maker
 * price was never stamped, which is exactly the `cycle_parked` / recovery
 * shape that lands on this screen, emits `0.0`. Rendering it prints
 * `filled $0.00` and, worse, feeds a SUM that then reads cheap and skips the
 * above-$1 mark: a cycle whose maker cost is UNKNOWN presented as a bargain.
 * Same rule the unwinder's Slack builder already applies (`counterPrice > 0`).
 */
function price(value: number | null | undefined): number | null {
  const n = num(value);
  return n !== null && n > 0 ? n : null;
}

/** Contract price the way the venues quote it — Polymarket ticks at $0.001. */
function usd(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })}`;
}

/**
 * What the hedge annotation is allowed to say, in the only grammar its numbers
 * support.
 *
 * 🚨 *at least*, always. `hedgedQty` is the venue ack's figure and the ack
 * UNDER-reports — Polymarket acks `delayed` / `unmatched` with `filled_qty: 0`
 * and fills afterwards — so it is a floor on what crossed, and `restedQty` a
 * ceiling on what is still open. `of Y` is only spoken when the hedge carried
 * the residual, because Y is `hedged + rested` and there is no Y without it;
 * `@ price` only when it carried a price. Nothing here is defaulted: a `0` the
 * backend never sent would read as *covered nothing* / *crossed free*.
 */
function coverageText(annotation: CounterStuckAutoClear): string {
  const rested = num(annotation.restedQty);
  const price = num(annotation.crossedPrice);
  const covered =
    rested === null
      ? `at least ${annotation.hedgedQty}`
      : `at least ${annotation.hedgedQty} of ${annotation.hedgedQty + rested}`;
  return `${covered} covered${price === null ? '' : ` @ ${usd(price)}`}`;
}

/** The bounds, spelled out — the chip and the line both carry it on hover. */
const COVERAGE_TOOLTIP =
  'Venue acks under-report fills, so the covered figure is a floor and the residual a ceiling.';

/**
 * The card is UNREAD and yet a hedge annotated it: the engine crossed part of
 * the tail and re-rested the rest. Without this line the row reads as an
 * ordinary `counter_stuck` quoting its own `unhedgedQty` — a frozen number
 * from a different instant, which the backend documents as NOT comparable with
 * the hedge's.
 */
function PartialHedgeLine({
  annotation,
}: {
  annotation: CounterStuckAutoClear;
}) {
  return (
    <Tooltip title={COVERAGE_TOOLTIP}>
      <Typography
        variant="caption"
        sx={{ color: TONE.warn, fontWeight: 700, display: 'block' }}
      >
        partially hedged at market — {coverageText(annotation)} — residual open
      </Typography>
    </Tooltip>
  );
}

/**
 * The episode is over and the card is STILL unread: the engine reported the
 * counter no longer stuck, but with contracts left un-hedged.
 *
 * This is the case the auto-clear deliberately refuses to clear, and without
 * the line the row would look identical to one whose episode is still running
 * — quoting a `restingSecs` that stopped advancing minutes ago. `at least` is
 * NOT used here: the residual is the engine's never-understate estimator, so
 * it is an upper bound on what is open, and the honest word is *up to*.
 */
function UnresolvedResidualLine({
  annotation,
}: {
  annotation: CounterStuckUnstuckClear;
}) {
  const rested = num(annotation.nakedQty);
  return (
    <Tooltip title="The engine's residual errs upward, so this is a ceiling on what is still open — never a floor.">
      <Typography
        variant="caption"
        sx={{ color: TONE.warn, fontWeight: 700, display: 'block' }}
      >
        no longer stuck
        {rested === null ? '' : ` — up to ${rested} still un-hedged`} — nothing
        is watching this leg any more
      </Typography>
    </Tooltip>
  );
}

const MUTED_ROW_SX = { opacity: 0.55 } as const;

/**
 * The page rendered while there is no response. Every count is zero, including
 * the hidden ones — with `data === undefined` the tray renders the UNREADABLE
 * alert instead of these trays, so this stands in for the shape and never for
 * a claim.
 */
function emptyBucket<T>(): TriageBucketPage<T> {
  return {
    unread: 0,
    hidden: 0,
    hiddenUnattributed: 0,
    hiddenCapped: false,
    items: [],
  };
}

/* ── Rows ─────────────────────────────────────────────────────────────── */

/**
 * The escalation line, and the fix for the blind spot in §3.1: a
 * `marketHedgeWindowSecs` of `null` is the TERMINAL case — no market hedge
 * will ever fire, so this card is the last word on those contracts — and the
 * payload drawer used to render no row at all for it, making the worst case
 * the only invisible one.
 */
function MarketHedgeWindowLine({ payload }: { payload: CounterStuckPayload }) {
  if (hasNoMarketHedgeWindow(payload.marketHedgeWindowSecs)) {
    return (
      <Typography
        variant="caption"
        sx={{ color: TONE.alarm, fontWeight: 700, display: 'block' }}
      >
        no market-hedge window — this card is the last word on these contracts
      </Typography>
    );
  }
  return (
    <Typography variant="caption" sx={{ display: 'block', color: TONE.label }}>
      escalates to a market hedge in {payload.marketHedgeWindowSecs}s
    </Typography>
  );
}

function RuleLink({
  ruleId,
  label,
}: {
  ruleId: string | null | undefined;
  label: string;
}) {
  if (!ruleId) return <>{label}</>;
  return (
    <Tooltip title="Open every notification for this rule">
      <Link
        component={RouterLink}
        to={`/predictions/notifications?tab=notifications&ruleId=${ruleId}`}
        underline="hover"
        color="inherit"
      >
        {label}
      </Link>
    </Tooltip>
  );
}

function ReadStateControls({
  evt,
  onRead,
  onUnread,
  busy,
  autoCleared,
}: {
  evt: Pick<TriageNotification, 'id' | 'occurredAt' | 'readAt' | 'readBy'>;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
  /** The hedge annotation, when the card carries one. */
  autoCleared?: CounterStuckAutoClear | null;
}) {
  const read = !!evt.readAt;
  const cleared = read && isAutoCleared(evt);
  // WHICH machine cleared it is the first thing an operator asks of an
  // auto-cleared card, and the two answers are different facts: one says the
  // engine crossed the book to cover the leg, the other that the leg stopped
  // being stuck and came out flat. `readBy` is the discriminator, because it
  // names the writer; the annotations do not — since the split they can BOTH
  // be present, which is the point of the split.
  const unstuck = cleared && isUnstuckCleared(evt);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" sx={{ color: TONE.label }}>
        {timeAgo(evt.occurredAt)}
      </Typography>
      {cleared && (
        <Tooltip title={autoCleared ? COVERAGE_TOOLTIP : ''}>
          <Chip
            size="small"
            variant="outlined"
            sx={tintedChipSx(TONE.ok)}
            label={
              // 🚨 The two are ADDITIVE. The whole reason the backend split
              // the annotation into its own field is that a partial hedge and
              // an episode ending are both true of the same card — 45 of 50
              // crossed at market, and by the time the episode ended the other
              // 5 were covered. Dropping the hedge half here throws away
              // exactly the datum that split went to rescue, and
              // `PartialHedgeLine` cannot show it either (it is gated on the
              // card still being unread).
              unstuck
                ? autoCleared
                  ? `auto-cleared — episode over, leg flat · ${coverageText(
                      autoCleared,
                    )}`
                  : 'auto-cleared — episode over, leg flat'
                : autoCleared
                  ? `auto-cleared — engine hedged at market · ${coverageText(
                      autoCleared,
                    )}`
                  : 'auto-cleared — engine hedged at market'
            }
          />
        </Tooltip>
      )}
      {read ? (
        <Button size="small" disabled={busy} onClick={() => onUnread(evt.id)}>
          Unread
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => onRead(evt.id)}
        >
          Mark read
        </Button>
      )}
    </Stack>
  );
}

export function CounterStuckRow({
  evt,
  onRead,
  onUnread,
  busy,
}: {
  evt: CounterStuckNotification;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
}) {
  const payload = (evt.payload ?? {}) as CounterStuckPayload;
  const parked = payload.kind === 'cycle_parked';
  const autoCleared = autoClearOf(payload);
  const unstuckResidual = unstuckClearOf(payload);
  const name = evt.pairName ?? evt.pairId ?? 'unnamed pair';
  const makerPrice = price(payload.makerFillPrice);
  const counterPrice = price(payload.counterPrice);
  const sum =
    makerPrice !== null && counterPrice !== null ? makerPrice + counterPrice : null;
  const overCap = sum !== null && sum >= 1;
  return (
    <Box sx={{ py: 1, ...(evt.readAt ? MUTED_ROW_SX : {}) }} data-triage-id={evt.id}>
      {/* Two columns. The left one is the identity and the two legs — the
          prose an operator reads once; the right one is the numbers they
          scan, right-aligned so they line up down the tray. The row used to
          be a single left-aligned column with the whole right half empty. */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: TONE.title, minWidth: 0 }}
            >
              <RuleLink ruleId={evt.ruleId} label={name} />
            </Typography>
            <TriageGroupChip group={evt.group} />
          </Stack>

          {parked && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: TONE.warn, fontWeight: 700 }}
            >
              cycle PARKED
            </Typography>
          )}

          {/* The two legs, one line each and aligned on the same label
              column, because the whole question the operator is asking is
              "which side is naked and against what". */}
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography
              variant="caption"
              sx={{ color: TONE.label, minWidth: 52, flexShrink: 0 }}
            >
              naked
            </Typography>
            <Typography variant="caption" sx={{ color: TONE.body }}>
              <Venue platform={payload.makerPlatform} />{' '}
              <Side side={payload.makerSide} />
              {makerPrice === null ? '' : ` filled ${usd(makerPrice)}`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography
              variant="caption"
              sx={{ color: TONE.label, minWidth: 52, flexShrink: 0 }}
            >
              counter
            </Typography>
            <Typography variant="caption" sx={{ color: TONE.body }}>
              <Venue platform={payload.platform} />{' '}
              <Side side={payload.counterSide} />
              {counterPrice === null ? '' : ` resting ${usd(counterPrice)}`}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: TONE.label }}>
            path {String(payload.path ?? '?')}
          </Typography>
          <MarketLinks markets={evt.markets} />
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 104 }}>
          <Typography
            variant="body2"
            sx={{ color: TONE.warn, fontWeight: 700, lineHeight: 1.2 }}
          >
            {qty(payload.unhedgedQty)}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: TONE.label }}>
            un-hedged
          </Typography>
          {!parked && (
            <Typography variant="caption" sx={{ display: 'block', color: TONE.label }}>
              resting {qty(payload.restingSecs)}s
            </Typography>
          )}
          {/* The sum is what the two prices are FOR: the per-contract cost
              this cycle commits to if the counter fills where it sits, and at
              or above $1 it is a loss whatever happens next. Only when both
              legs are known — a sum missing one is not a sum. */}
          {sum !== null && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                fontWeight: overCap ? 700 : 600,
                color: overCap ? TONE.alarm : TONE.body,
              }}
            >
              sum {usd(sum)}
              {overCap && (
                <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
                  at or above $1
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </Stack>
      <MarketHedgeWindowLine payload={payload} />
      {!evt.readAt && autoCleared && (
        <PartialHedgeLine annotation={autoCleared} />
      )}
      {!evt.readAt && unstuckResidual && (
        <UnresolvedResidualLine annotation={unstuckResidual} />
      )}
      <ReadStateControls
        evt={evt}
        onRead={onRead}
        onUnread={onUnread}
        busy={busy}
        autoCleared={autoCleared}
      />
    </Box>
  );
}

export function OrphanedFillRow({
  evt,
  onRead,
  onUnread,
  busy,
}: {
  evt: OrphanedFillNotification;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
}) {
  const payload = (evt.payload ?? {}) as OrphanedAutomationFillPayload;
  const title = payload.marketTitle ?? payload.marketId ?? 'unnamed market';
  const attributed = payload.attributedBy === 'market-identity';
  const coid = payload.clientOrderId ?? null;
  const rulePrefix = payload.ruleIdPrefix ?? null;
  return (
    <Box sx={{ py: 1, ...(evt.readAt ? MUTED_ROW_SX : {}) }} data-triage-id={evt.id}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: TONE.title, minWidth: 0 }}
        >
          {payload.marketUrl ? (
            <Link
              href={payload.marketUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              {title}
              <OpenInNewIcon sx={{ fontSize: '0.8rem', ml: 0.3 }} />
            </Link>
          ) : (
            title
          )}
        </Typography>
        <TriageGroupChip group={evt.group} />
        {!attributed && (
          <Chip
            size="small"
            variant="outlined"
            label="unattributed"
            sx={tintedChipSx(TONE.warn)}
          />
        )}
      </Stack>
      {/* Same two-column shape as the counter row: prose left, the number
          the operator scans right. */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', color: TONE.body }}>
            <Venue platform={payload.platform} />
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography
              variant="caption"
              sx={{ color: TONE.label, minWidth: 52, flexShrink: 0 }}
            >
              reason
            </Typography>
            <Typography variant="caption" sx={{ color: TONE.body }}>
              {String(payload.reason ?? 'unknown')}
            </Typography>
          </Stack>
          {(coid || rulePrefix) && (
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography
                variant="caption"
                sx={{ color: TONE.label, minWidth: 52, flexShrink: 0 }}
              >
                {coid ? 'coid' : 'rule'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: TONE.body, wordBreak: 'break-all' }}
              >
                {coid ?? rulePrefix}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography
              variant="caption"
              sx={{ color: TONE.label, minWidth: 52, flexShrink: 0 }}
            >
              pair
            </Typography>
            {attributed ? (
              <Typography variant="caption" sx={{ color: TONE.body }}>
                {evt.pairName ?? evt.pairId ?? 'unnamed pair'}
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={{ color: TONE.warn, fontWeight: 600 }}
              >
                could NOT be tied to one — no row anywhere carries its mark
              </Typography>
            )}
          </Stack>
          <MarketLinks markets={evt.markets} />
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0, minWidth: 104 }}>
          <Typography
            variant="body2"
            sx={{ color: TONE.warn, fontWeight: 700, lineHeight: 1.2 }}
          >
            {qty(payload.filledQty)}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: TONE.label }}>
            filled
          </Typography>
        </Box>
      </Stack>
      <ReadStateControls evt={evt} onRead={onRead} onUnread={onUnread} busy={busy} />
    </Box>
  );
}

/* ── Trays ────────────────────────────────────────────────────────────── */

/* ── The feed ─────────────────────────────────────────────────────────── */

/**
 * What each card IS, as a chip.
 *
 * The two kinds used to be two side-by-side trays, so the column said what
 * the card was. In one merged feed nothing does, and they are not
 * interchangeable: a counter stuck is a leg the engine is still working, an
 * orphaned fill is money nothing owns. The chip carries that distinction now.
 */
const KIND_LABEL: Record<string, { label: string; color: string }> = {
  counter_stuck: { label: 'counter stuck', color: '#e3a857' },
  orphaned_automation_fill: { label: 'orphaned fill', color: '#c98bd9' },
};

function KindChip({ type }: { type: string }) {
  const kind = KIND_LABEL[type] ?? { label: type, color: '#9e9e9e' };
  return (
    <Chip
      size="small"
      variant="outlined"
      label={kind.label}
      data-triage-kind={type}
      sx={tintedChipSx(kind.color)}
    />
  );
}

/**
 * ONE scrollable feed over both card kinds, newest first.
 *
 * It replaced two side-by-side trays. The trays split the screen by a
 * distinction the operator does not triage by — they work through what is
 * open, oldest exposure first, whatever kind it is — and each got half the
 * width for it, which is what left the rows cramped into a column with the
 * other half empty.
 *
 * 🚨 The merge is a RENDER, not a fetch: the two buckets are still fetched,
 * counted, scoped and paginated separately server-side (each keysets on its
 * own cursor and one cannot be replayed against the other). So this list is
 * the union of the two FIRST pages, and the header reports the server's own
 * totals rather than `items.length` — a feed that counted what it merged
 * would under-report the moment either bucket exceeded its page.
 */
function TriageFeed({
  counter,
  orphan,
  renderCounter,
  renderOrphan,
  onMarkAllShown,
  busy,
  scoped,
}: {
  counter: TriageBucketPage<CounterStuckPayload>;
  orphan: TriageBucketPage<OrphanedAutomationFillPayload>;
  renderCounter: (evt: CounterStuckNotification) => ReactNode;
  renderOrphan: (evt: OrphanedFillNotification) => ReactNode;
  onMarkAllShown: (ids: string[]) => void;
  busy: boolean;
  /** A scope is hiding cards: the chip must not read as the whole feed. */
  scoped: boolean;
}) {
  const items = useMemo(() => {
    const merged = [
      ...counter.items.map((evt) => ({ evt, kind: 'counter' as const })),
      ...orphan.items.map((evt) => ({ evt, kind: 'orphan' as const })),
    ];
    // Newest first, and `seq` breaks the tie the same way the server's own
    // keyset does — two cards can share a millisecond.
    merged.sort((a, b) => {
      const ta = Date.parse(a.evt.occurredAt);
      const tb = Date.parse(b.evt.occurredAt);
      if (tb !== ta) return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      return (b.evt.seq ?? 0) - (a.evt.seq ?? 0);
    });
    return merged;
  }, [counter.items, orphan.items]);

  // EXACTLY the ids on screen — never a query. Nothing the operator has not
  // seen can be silenced by this button, and a deep-link scope can only
  // narrow what is on screen, never widen it.
  const shownUnreadIds = useMemo(
    () => items.filter(({ evt }) => !evt.readAt).map(({ evt }) => evt.id),
    [items],
  );

  const unread = counter.unread + orphan.unread;
  const capped = !!counter.capped || !!orphan.capped;
  const hidden = counter.hidden + orphan.hidden;
  const hiddenCapped = counter.hiddenCapped || orphan.hiddenCapped;
  const hiddenUnattributed =
    counter.hiddenUnattributed + orphan.hiddenUnattributed;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: TONE.title }}
        >
          Open
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          data-triage-tray-unread={unread}
          sx={tintedChipSx(unread > 0 ? TONE.warn : '#9e9e9e')}
          label={
            scoped
              ? `${shownUnreadIds.length} of ${formatUnreadBadge(
                  unread,
                  capped,
                )} unread shown`
              : `${formatUnreadBadge(unread, capped)} unread`
          }
        />
      </Stack>
      {hidden > 0 && (
        <Tooltip title="Hidden by the pair scope, not marked read: their pair is finished / archived / expired / settled, or the card names no pair at all. The toggle above brings them back.">
          <Typography
            variant="caption"
            sx={{ display: 'block', color: TONE.warn, fontWeight: 600 }}
            data-triage-hidden={hidden}
          >
            + {formatUnreadBadge(hidden, hiddenCapped)} unread hidden by the
            pair scope
            {hiddenUnattributed > 0
              ? ` (${formatUnreadBadge(
                  hiddenUnattributed,
                  hiddenCapped,
                )} name no pair)`
              : ''}
          </Typography>
        </Tooltip>
      )}
      <Divider sx={{ my: 1 }} />
      {items.length === 0 ? (
        <Typography variant="caption" sx={{ color: TONE.label }}>
          {scoped ? 'No card here matches the filter.' : 'Nothing open.'}
        </Typography>
      ) : (
        <Box
          sx={{ maxHeight: '68vh', overflowY: 'auto', overflowAnchor: 'auto' }}
          data-triage-feed="1"
        >
          {items.map(({ evt, kind }) => (
            <Box key={evt.id}>
              <Box sx={{ pt: 1 }}>
                <KindChip type={evt.type} />
              </Box>
              {kind === 'counter'
                ? renderCounter(evt as CounterStuckNotification)
                : renderOrphan(evt as OrphanedFillNotification)}
              <Divider />
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ pt: 1 }}>
        <Button
          size="small"
          disabled={busy || shownUnreadIds.length === 0}
          onClick={() => onMarkAllShown(shownUnreadIds)}
        >
          Mark all shown read
        </Button>
      </Box>
    </Paper>
  );
}

/* ── Deep-link scope ──────────────────────────────────────────────────── */

/**
 * A mark on a row is a pointer at SOME cards, so the tray it opens has to say
 * which ones. It FILTERS rather than highlights: the trays share one
 * `Mark all shown read` button whose scope is "the ids on screen", and a
 * highlight would leave that button covering 180 cards while the operator
 * believes they are looking at one row's.
 *
 * 🚨 A filtered tray must never present as the whole tray — that is the same
 * "clean screen over an open leg" the feature exists to prevent — so the
 * banner is unconditional, says what it is hiding, and clears in one click.
 *
 * What it can count is THIS PAGE: `hidden` is the cards the fetched page holds
 * and the scope rejects, and the page is capped (`TRIAGE_LIST_LIMIT`). With 300
 * unread and 100 fetched, "98 hidden" would be 198 short — and with *show read*
 * on it also counts cleared cards. So the line says *of this page*, and the
 * bucket chip beside it keeps reporting the server's own unread total.
 */
function scopedBucket<TPayload>(
  page: TriageBucketPage<TPayload>,
  scope: TriageScope | null,
): { page: TriageBucketPage<TPayload>; hidden: number } {
  const items = applyTriageScope(page.items, scope);
  return { page: { ...page, items }, hidden: page.items.length - items.length };
}

function scopeLabel(scope: TriageScope): string {
  if (scope.kind === 'groupId' && scope.id === TRIAGE_UNGROUPED_KEY) {
    return TRIAGE_UNGROUPED_LABEL;
  }
  return `${TRIAGE_SCOPE_NOUN[scope.kind]} ${scope.id}`;
}

function TriageScopeBanner({
  scope,
  shown,
  hidden,
  onClear,
}: {
  scope: TriageScope;
  shown: number;
  hidden: number;
  onClear: () => void;
}) {
  // The `unfilterable` arm is GONE, not disabled: a card now carries the
  // server's group attribution, so every scope kind filters and the branch
  // that admitted otherwise was unreachable. An unreachable warning is worse
  // than none — a test can pin it and prove nothing.
  const clear = (
    <Button size="small" onClick={onClear} data-triage-scope-clear="1">
      Show all cards
    </Button>
  );

  return (
    <Alert
      severity="info"
      action={clear}
      data-triage-scope="filtered"
      sx={{ mt: 1 }}
    >
      Filtered to {scopeLabel(scope)} — showing {shown} card
      {shown === 1 ? '' : 's'}, {hidden} more on this page hidden by the
      filter. The tray is paged, so this counts only what was fetched.
    </Alert>
  );
}

/* ── The tab ──────────────────────────────────────────────────────────── */

export function TriageTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showRead = searchParams.get(TRIAGE_SHOW_READ_PARAM) === '1';
  const state: TriageStateFilter = showRead ? 'all' : 'unread';
  const includeRetired =
    searchParams.get(TRIAGE_INCLUDE_RETIRED_PARAM) === '1';

  const setParamFlag = (key: string, next: boolean) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set(key, '1');
        else p.delete(key);
        return p;
      },
      { replace: true },
    );
  };
  const setShowRead = (next: boolean) =>
    setParamFlag(TRIAGE_SHOW_READ_PARAM, next);
  const setIncludeRetired = (next: boolean) =>
    setParamFlag(TRIAGE_INCLUDE_RETIRED_PARAM, next);

  const scope = readTriageScope(searchParams);
  // Every scope kind filters now — the card carries the server's own group
  // attribution, which is what the group arm was missing.
  const filtering = scope !== null;
  const clearScope = () => {
    setSearchParams((prev) => withoutTriageScope(new URLSearchParams(prev)), {
      replace: true,
    });
  };

  const { data, isStale, lastSuccessAt } = useTriageList({
    state,
    includeRetired,
  });
  const markRead = useMarkTriageRead();
  const markUnread = useMarkTriageUnread();
  const busy = markRead.isLoading || markUnread.isLoading;

  const counter = scopedBucket<CounterStuckPayload>(
    data?.counter ?? emptyBucket<CounterStuckPayload>(),
    scope,
  );
  const orphan = scopedBucket<OrphanedAutomationFillPayload>(
    data?.orphan ?? emptyBucket<OrphanedAutomationFillPayload>(),
    scope,
  );

  // Across both buckets: the banner is about the class, not about which tray
  // it landed in.
  const unattributedHidden =
    (data?.counter.hiddenUnattributed ?? 0) +
    (data?.orphan.hiddenUnattributed ?? 0);

  // 🚨 What is ON SCREEN, not what the URL asked for. `keepPreviousData` keeps
  // the previous scope's response through the toggle's round-trip, so the two
  // disagree for one fetch — and the wrong half to trust is the URL: it would
  // render the widened counts with no banner at all, which is the number this
  // whole scope exists to stop showing.
  const showingRetired = data?.includesRetired === true;

  const onRead = (id: string) => markRead.mutate([id]);
  const onUnread = (id: string) => markUnread.mutate([id]);
  const onMarkAllShown = (ids: string[]) => {
    if (ids.length === 0) return;
    // The count is in the confirm because the button's scope is what is on
    // screen right now, and that is not obvious from its label.
    if (!window.confirm(`Mark ${ids.length} shown cards read?`)) return;
    markRead.mutate(ids);
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        {/* Explicitly white. The theme's `text.primary` resolves to a muted
            dark grey against this app's chrome, so a header that leans on it
            is nearly invisible — and this one carries the sentence the whole
            screen is about. */}
        <Typography variant="body2" sx={{ color: TONE.title, fontWeight: 600 }}>
          {data
            ? data.oldestUnreadAt
              ? `Oldest unread: ${timeAgo(data.oldestUnreadAt)}`
              : 'Nothing unread.'
            : ''}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showRead}
                onChange={(e) => setShowRead(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: TONE.body }}>
                show read (last 24h)
              </Typography>
            }
          />
          <Tooltip title="Off by default: a card on a market that already finished names a leg nobody can act on any more. They are NOT marked read — this brings them straight back.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={includeRetired}
                  // On the INPUT, not the root: MUI spreads unknown props
                  // onto the SwitchBase span, where nothing can click them.
                  inputProps={
                    {
                      'data-triage-include-retired': includeRetired ? '1' : '0',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  onChange={(e) => setIncludeRetired(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: TONE.body }}>
                  include finished / archived / expired pairs
                </Typography>
              }
            />
          </Tooltip>
        </Stack>
      </Stack>

      {/* 🚨 Keyed on the DATA, never on `isError`: react-query's error flag is
          sticky and retains `data`, so a tray that read it as truth would go
          blank over data it still had — and a tray with no rows asserts "no
          naked legs". */}
      {data === undefined ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Triage unreadable — the tray could not be loaded. This is NOT
          &quot;nothing open&quot;.
        </Alert>
      ) : (
        <>
          {scope && (
            <TriageScopeBanner
              scope={scope}
              shown={counter.page.items.length + orphan.page.items.length}
              hidden={counter.hidden + orphan.hidden}
              onClear={clearScope}
            />
          )}
          {/* 🚨 The scope is an ALLOW-list: it hides every card it cannot tie
              to a live pair, and an orphaned fill that names no pair is
              exactly that — the least classifiable naked money in the system.
              Hiding them is the operator's setting; hiding them in silence is
              not, so this banner is unconditional whenever there are any, and
              it says they are still unread. */}
          {unattributedHidden > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 1 }}
              data-triage-hidden-unattributed={unattributedHidden}
              action={
                <Button size="small" onClick={() => setIncludeRetired(true)}>
                  Show them
                </Button>
              }
            >
              {unattributedHidden} unread card
              {unattributedHidden === 1 ? '' : 's'} could NOT be tied to a pair
              and {unattributedHidden === 1 ? 'is' : 'are'} hidden by the pair
              scope. They are still UNREAD — nothing was cleared.
            </Alert>
          )}
          {showingRetired && (
            <Alert severity="info" sx={{ mt: 1 }} data-triage-retired="included">
              Showing cards on FINISHED, archived, expired and Kalshi-settled
              pairs too. Those name legs on markets that are over — the counts
              below include them.
            </Alert>
          )}
          {/* Not a detail: the scope is what makes these counts mean
              "actionable", and when the server could not resolve it the tray
              is the unscoped one again. Saying nothing would let the operator
              read a number as something it is not. */}
          {data.pairScopeCapped && (
            <Alert severity="warning" sx={{ mt: 1 }} data-triage-scope-capped="1">
              Too many distinct pairs to scope — the tray below is UNFILTERED
              and includes cards on pairs that are already over.
            </Alert>
          )}
          {isStale && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Stale — last read{' '}
              {lastSuccessAt ? timeAgo(new Date(lastSuccessAt).toISOString()) : 'a while ago'}.
            </Alert>
          )}
          <Box sx={{ mt: 1.5 }}>
            <TriageFeed
              counter={counter.page}
              orphan={orphan.page}
              scoped={filtering}
              busy={busy}
              onMarkAllShown={onMarkAllShown}
              renderCounter={(evt) => (
                <CounterStuckRow
                  evt={evt}
                  onRead={onRead}
                  onUnread={onUnread}
                  busy={busy}
                />
              )}
              renderOrphan={(evt) => (
                <OrphanedFillRow
                  evt={evt}
                  onRead={onRead}
                  onUnread={onUnread}
                  busy={busy}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
